/**
 * Entry point for PDF info building.
 * Each document type has its own builder in pdfBuilders/.
 *
 * Splitting guide:
 *   pdfBuilders/buildTaxInvoice.ts   — TAX_INVOICE
 *   pdfBuilders/buildInvoice.ts      — INVOICE
 *   pdfBuilders/buildReceipt.ts      — RECEIPT, TRANSFER_RECEIPT
 *   pdfBuilders/buildCreditDebit.ts  — CREDIT_DEBIT, DEBIT_CREDIT, PRQ
 *   pdfBuilders/buildAgentInvoice.ts — AGENT_INVOICE
 *   pdfBuilders/buildRemittance.ts   — REMITTANCE
 *   pdfBuilders/buildDeposit.ts      — DEPOSIT, WITHDRAWAL
 *   pdfBuilders/buildStatement.ts    — STATEMENT
 *   pdfBuilders/buildServiceVoucher.ts — VOUCHER, REFUND, EXTENDED_REFUND
 */

import { Voucher } from './types';
import { documentTypesEnum } from './helpers';

// Re-export types for external consumers
export type { PdfInfoBuilderContext, BuiltPdfInfo } from './pdfBuilders/types';
export { normalizeVoucherInfo, buildBasePdfInfo } from './pdfBuilders/helpers';

import { PdfInfoBuilderContext, BuiltPdfInfo } from './pdfBuilders/types';
import { normalizeVoucherInfo, buildBasePdfInfo } from './pdfBuilders/helpers';
import { buildTaxInvoicePdfInfo } from './pdfBuilders/buildTaxInvoice';
import { buildInvoicePdfInfo } from './pdfBuilders/buildInvoice';
import { buildReceiptPdfInfo } from './pdfBuilders/buildReceipt';
import { buildCreditDebitPdfInfo, buildPRQPdfInfo } from './pdfBuilders/buildCreditDebit';
import { buildAgentInvoicePdfInfo } from './pdfBuilders/buildAgentInvoice';
import { buildRemittancePdfInfo } from './pdfBuilders/buildRemittance';
import { buildDepositPdfInfo, buildWithdrawalPdfInfo } from './pdfBuilders/buildDeposit';
import { buildStatementPdfInfo } from './pdfBuilders/buildStatement';
import { buildServiceVoucherPdfInfo, buildServiceRefundPdfInfo } from './pdfBuilders/buildServiceVoucher';

/**
 * Main entry point — builds pdfInfo based on document type.
 */
export function buildPdfInfo(voucher: Voucher, context: PdfInfoBuilderContext): BuiltPdfInfo {
    voucher = normalizeVoucherInfo(voucher);
    const baseInfo = buildBasePdfInfo(voucher, context);

    switch (voucher.type) {
        case documentTypesEnum.TAX_INVOICE:
            return buildTaxInvoicePdfInfo(voucher, context, baseInfo);
        case documentTypesEnum.INVOICE:
            return buildInvoicePdfInfo(voucher, context, baseInfo);
        case documentTypesEnum.RECEIPT:
        case documentTypesEnum.TRANSFER_RECEIPT:
            return buildReceiptPdfInfo(voucher, context, baseInfo);
        case documentTypesEnum.CREDIT_DEBIT:
        case documentTypesEnum.DEBIT_CREDIT:
            return buildCreditDebitPdfInfo(voucher, context, baseInfo);
        case documentTypesEnum.PRQ:
            return buildPRQPdfInfo(voucher, context, baseInfo);
        case documentTypesEnum.AGENT_INVOICE:
            return buildAgentInvoicePdfInfo(voucher, context, baseInfo);
        case documentTypesEnum.REMITTANCE:
            return buildRemittancePdfInfo(voucher, context, baseInfo);
        case documentTypesEnum.DEPOSIT:
            return buildDepositPdfInfo(voucher, context, baseInfo);
        case documentTypesEnum.WITHDRAWAL:
            return buildWithdrawalPdfInfo(voucher, context, baseInfo);
        case documentTypesEnum.STATEMENT:
            return buildStatementPdfInfo(voucher, context, baseInfo);
        case documentTypesEnum.VOUCHER:
            return buildServiceVoucherPdfInfo(voucher, context, baseInfo);
        case documentTypesEnum.REFUND:
        case documentTypesEnum.EXTENDED_REFUND:
            return buildServiceRefundPdfInfo(voucher, context, baseInfo);
        default:
            return baseInfo;
    }
}
